import React, { useEffect, useRef, useState } from 'react';
import {
    Button,
    FormControl,
    MenuItem,
    Paper,
    TextField,
    Typography,
    makeStyles,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Popper from '@material-ui/core/Popper';
import HttpService from '../services/httpService';
import { ROLENAME, salesDepartment } from '../utils/index';

const NAVY = '#1C375D';
const ORANGE = '#F47E46';
const CREAM = '#FFF2EB';

const searchTypes = ['Customer Name', 'Email ID', 'Lead No', 'Project No', 'Contact No', 'Unique Lead No'];

/** Prevents Select from closing before MenuItem click registers (common MUI + accordion issue). */
const selectMenuProps = {
    getContentAnchorEl: null,
    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
    transformOrigin: { vertical: 'top', horizontal: 'left' },
    disablePortal: false,
    disableScrollLock: true,
    MenuListProps: {
        onMouseDown: (event) => {
            event.preventDefault();
        },
    },
    PaperProps: {
        style: { zIndex: 9999, maxHeight: 320 },
    },
};

const filterAutocompletePopper = (popperProps) => (
    <Popper
        {...popperProps}
        style={{ ...popperProps.style, zIndex: 9999 }}
        placement="bottom-start"
        disablePortal={false}
    />
);

const useStyles = makeStyles((theme) => ({
    paper: {
        padding: theme.spacing(2.5, 3),
        marginBottom: theme.spacing(2),
        textAlign: 'left',
        borderRadius: 8,
        border: '1px solid #e5ebf2',
        boxShadow: '0 1px 3px rgba(28, 55, 93, 0.08)',
        backgroundColor: '#fff',
    },
    sectionLabel: {
        color: NAVY,
        fontWeight: 600,
        fontSize: 13,
        letterSpacing: '0.02em',
        marginBottom: theme.spacing(1.5),
    },
    formControl: {
        width: '100%',
    },
    filterRows: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.5),
        '& .MuiOutlinedInput-root': {
            height: 40,
            borderRadius: 4,
        },
        '& .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"]': {
            minHeight: 40,
            height: 40,
            paddingTop: 0,
            paddingBottom: 0,
            alignItems: 'center',
        },
        '& .MuiAutocomplete-tag': {
            margin: 2,
            maxHeight: 24,
        },
    },
    filterRowFlex: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 12,
        alignItems: 'flex-end',
    },
    filterCell: {
        flex: '1 1 180px',
        width: 180,
        maxWidth: 180,
        minWidth: 160,
    },
    filterCellGrow: {
        flex: '2 1 180px',
        minWidth: 180,
        maxWidth: 280,
    },
    filterButtonBase: {
        height: 40,
        minHeight: 40,
        width: '100%',
        borderRadius: 4,
        boxShadow: 'none',
        textTransform: 'none',
        fontWeight: 600,
        fontSize: 14,
        lineHeight: 1.2,
    },
    actionBtn: {
        backgroundColor: ORANGE,
        color: CREAM,
        '&:hover': {
            backgroundColor: '#E0663D',
            boxShadow: 'none',
        },
    },
    searchBtn: {
        backgroundColor: '#FDB913',
        color: '#151921',
        '&:hover': {
            backgroundColor: '#14419c',
            color: '#fff',
            boxShadow: 'none',
        },
        '&:disabled': {
            backgroundColor: '#e5e7eb',
            color: '#9ca3af',
        },
    },
    clearBtn: {
        backgroundColor: '#f7b7b7',
        color: '#151921',
        whiteSpace: 'nowrap',
        '&:hover': {
            backgroundColor: '#f0a0a0',
            boxShadow: 'none',
        },
    },
    embeddedRoot: {
        width: '100%',
        boxSizing: 'border-box',
        overflow: 'visible',
    },
    gridItem: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
    },
}));

const parseIdList = (value) =>
    String(value || '')
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);

const matchByIds = (options = [], ids = []) =>
    options.filter((option) => ids.includes(option._id));

const resolveSelectedRole = (userDetails, selectedRole, usersData) => {
    if (usersData?.role) return usersData.role;
    if (userDetails?.roles?.find((role) =>
        [ROLENAME.OperationDesignManager, ROLENAME.AssistantDesignManager, ROLENAME.DesignUser].includes(role.name)
    )) {
        return selectedRole || ROLENAME.DesignUser;
    }
    if (userDetails?.roles?.find((role) =>
        [ROLENAME.SalesManager, ROLENAME.AssistantSalesManager, ROLENAME.SalesUser].includes(role.name)
    )) {
        return selectedRole || ROLENAME.SalesUser;
    }
    return selectedRole || 'ALL';
};

export const buildDesignSummaryFilterParams = ({
    userDetails,
    storeDateFilter,
    selectedLocation,
    selectedExperienceCenter,
    selectedValue,
    selectedUsers,
    selectedRole,
    selectedSearchType,
    searchInput,
    usersData,
}) => {
    const locationData = selectedLocation?.map((el) => el._id) || [];
    const expCenterData = selectedExperienceCenter?.map((el) => el._id) || [];
    let users = '';
    if ((usersData?.users && usersData.users.length) || (selectedUsers && selectedUsers.length)) {
        const userIds = usersData?.users?.map((el) => el._id) || selectedUsers?.map((el) => el._id);
        users = Array.isArray(userIds) ? userIds.join(',') : '';
    }

    return {
        startDate: storeDateFilter.startDate || '',
        endDate: storeDateFilter.endDate || '',
        selectedLocation: locationData.join(','),
        selectedExperienceCenter: expCenterData.join(','),
        token: selectedValue !== '' ? selectedValue : '',
        users,
        selectedRole: resolveSelectedRole(userDetails, selectedRole, usersData),
        userExpCenter: (userDetails?.experienceCenterId || []).join(','),
        userLocation: (userDetails?.selectedLocation || []).join(','),
        searchType: selectedSearchType || '',
        search: searchInput ? searchInput.toUpperCase() : '',
    };
};

const DesignSummaryFilterPanel = ({
    userDetails,
    initialParams = {},
    onApply,
    setSnackInfo,
    embedded = false,
}) => {
    const classes = useStyles();
    const hydratedRef = useRef(false);

    const [commonExpCenter, setExpCenter] = useState([]);
    const [commonLocation, setLocation] = useState([]);
    const [storeDateFilter, setStoreDateFilter] = useState({
        startDate: initialParams.startDate || '',
        endDate: initialParams.endDate || '',
    });
    const [selectedExperienceCenter, setSelectedExperienceCenter] = useState([]);
    const [selectedLocation, setSelectedLocation] = useState([]);
    const [selectedValue, setSelectedValue] = useState(
        initialParams.token !== undefined && initialParams.token !== '' ? initialParams.token : '2'
    );
    const [selectedRole, setSelectedRole] = useState(initialParams.selectedRole || '');
    const [usersData, setUsersData] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [selectedSearchType, setSelectedSearchType] = useState(initialParams.searchType || '');
    const [searchInput, setSearchInput] = useState(initialParams.search || '');

    const hasRole = (...roles) =>
        userDetails?.roles?.some((role) => roles.includes(role.name));

    const canPickLocation =
        hasRole(
            ROLENAME.CenterHead,
            ROLENAME.BusinessHead,
            ROLENAME.DesignHead,
            ROLENAME.AGM_Design,
            ROLENAME.Admin,
            ROLENAME.DataAnalyst,
            ROLENAME.OperationDesignManager,
            ROLENAME.AssistantDesignManager,
            ROLENAME.DesignUser,
            ROLENAME.DGM_Sales,
            ROLENAME.FinanceUser,
            ROLENAME.FinanceManager,
            ROLENAME.FinanceExecutive,
            ROLENAME.SalesManager,
            ROLENAME.AGMSales
        ) && (userDetails?.locationId?.length || 0) > 1;

    const canPickExpCenter =
        hasRole(
            ROLENAME.CenterHead,
            ROLENAME.BusinessHead,
            ROLENAME.DesignHead,
            ROLENAME.AGM_Design,
            ROLENAME.Admin,
            ROLENAME.DataAnalyst,
            ROLENAME.OperationDesignManager,
            ROLENAME.AssistantDesignManager,
            ROLENAME.DesignUser,
            ROLENAME.DGM_Sales,
            ROLENAME.FinanceUser,
            ROLENAME.FinanceManager,
            ROLENAME.FinanceExecutive,
            ROLENAME.SalesManager,
            ROLENAME.AGMSales
        ) && (userDetails?.experienceCenterId?.length || 0) > 1;

    const applyFilters = async (overrides = {}) => {
        const params = buildDesignSummaryFilterParams({
            userDetails,
            storeDateFilter: overrides.storeDateFilter || storeDateFilter,
            selectedLocation: overrides.selectedLocation ?? selectedLocation,
            selectedExperienceCenter: overrides.selectedExperienceCenter ?? selectedExperienceCenter,
            selectedValue: overrides.selectedValue ?? selectedValue,
            selectedUsers: overrides.selectedUsers ?? selectedUsers,
            selectedRole: overrides.selectedRole ?? selectedRole,
            selectedSearchType: overrides.selectedSearchType ?? selectedSearchType,
            searchInput: overrides.searchInput ?? searchInput,
            usersData: overrides.usersData,
        });
        if (typeof window !== 'undefined') {
            const url = new URL(window.location.href);
            Object.entries(params).forEach(([key, value]) => {
                if (value) url.searchParams.set(key, value);
                else url.searchParams.delete(key);
            });
            window.history.replaceState({}, '', url.toString());
        }
        await onApply(params);
    };

    const expCenters = async () => {
        try {
            const response = await HttpService.getExpericenCenters();
            const matched = response.data.filter((user) =>
                userDetails.experienceCenterId.includes(user._id)
            );
            setExpCenter(matched);
        } catch (error) {
            setSnackInfo(error?.response?.data ?? 'Something Went Wrong', 'error');
        }
    };

    const locations = async () => {
        try {
            const response = await HttpService.getLocation();
            const matched = response.data.filter((user) =>
                userDetails.selectedLocation.includes(user._id)
            );
            setLocation(matched);
        } catch (error) {
            setSnackInfo(error?.response?.data ?? 'Something Went Wrong', 'error');
        }
    };

    const fetchUsersData = async (roleOverride) => {
        let role = roleOverride;
        if (!role) {
            if (hasRole(ROLENAME.SalesManager, ROLENAME.AssistantSalesManager, ROLENAME.SalesUser)) {
                role = ROLENAME.SalesUser;
            } else if (hasRole(ROLENAME.OperationDesignManager, ROLENAME.AssistantDesignManager, ROLENAME.DesignUser)) {
                role = ROLENAME.DesignUser;
            } else {
                role = 'ALL';
            }
        }
        try {
            const response = await HttpService.getSelectedUsersBasedOnRoles({ role });
            setUsersData(response.data);
        } catch (error) {
            setSnackInfo(error?.response?.data ?? 'Something Went Wrong', 'error');
        }
    };

    useEffect(() => {
        if (!userDetails?._id) return;
        expCenters();
        locations();
        if (hasRole(ROLENAME.SalesManager, ROLENAME.AssistantSalesManager, ROLENAME.SalesUser)) {
            setSelectedRole((prev) => prev || ROLENAME.SalesUser);
        } else if (hasRole(ROLENAME.OperationDesignManager, ROLENAME.AssistantDesignManager, ROLENAME.DesignUser)) {
            setSelectedRole((prev) => prev || ROLENAME.DesignUser);
        } else {
            setSelectedRole((prev) => prev || 'ALL');
        }
        fetchUsersData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userDetails?._id]);

    useEffect(() => {
        if (hydratedRef.current || !commonLocation.length && !commonExpCenter.length) return;
        const locationIds = parseIdList(initialParams.selectedLocation);
        const expIds = parseIdList(initialParams.selectedExperienceCenter);
        if (locationIds.length) {
            setSelectedLocation(matchByIds(commonLocation, locationIds));
        }
        if (expIds.length) {
            setSelectedExperienceCenter(matchByIds(commonExpCenter, expIds));
        }
        hydratedRef.current = true;
    }, [commonLocation, commonExpCenter, initialParams.selectedLocation, initialParams.selectedExperienceCenter]);

    useEffect(() => {
        if (!usersData.length || !initialParams.users) return;
        const userIds = parseIdList(initialParams.users);
        if (userIds.length) {
            setSelectedUsers(matchByIds(usersData, userIds));
        }
    }, [usersData, initialParams.users]);

    const storePivotDate = (e) => {
        const { name, value } = e.target;
        setStoreDateFilter({ ...storeDateFilter, [name]: value });
    };

    const handlePivotDateFilter = () => {
        applyFilters();
    };

    const handleSelectChange = (event) => {
        setSelectedValue(event.target.value);
    };

    const handleRoleAutocompleteChange = (_, newValue) => {
        if (!newValue?.value) return;
        setSelectedUsers([]);
        setSelectedRole(newValue.value);
        HttpService.getSelectedUsersBasedOnRoles({ role: newValue.value })
            .then((response) => setUsersData(response.data))
            .catch((error) => {
                setSnackInfo(error?.response?.data ?? 'Something Went Wrong', 'error');
            });
    };

    const handleMultipleLocationData = (e, newValue) => {
        setSelectedLocation(newValue);
        applyFilters({ selectedLocation: newValue });
    };

    const handleMultipleExpData = (e, newValue) => {
        setSelectedExperienceCenter(newValue);
        applyFilters({ selectedExperienceCenter: newValue });
    };

    const handleMultipleUsers = (e, newValue) => {
        setSelectedUsers(newValue);
    };

    const handleSearchType = (value) => {
        setSelectedSearchType(value);
        setSearchInput('');
    };

    const handleSearch = () => {
        applyFilters();
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter' && selectedSearchType && searchInput) {
            handleSearch();
        }
    };

    const clearDateFilter = () => {
        const clearedDates = { startDate: '', endDate: '' };
        setSearchInput('');
        setSelectedSearchType('');
        setSelectedExperienceCenter([]);
        setSelectedLocation([]);
        setSelectedUsers([]);
        setSelectedValue('2');
        setStoreDateFilter(clearedDates);
        applyFilters({
            storeDateFilter: clearedDates,
            selectedSearchType: '',
            searchInput: '',
            selectedExperienceCenter: [],
            selectedLocation: [],
            selectedUsers: [],
            selectedValue: '2',
        });
    };

    const usersAutocomplete = (
        <FormControl className={classes.formControl}>
            <Autocomplete
                multiple
                size="small"
                value={selectedUsers}
                onChange={handleMultipleUsers}
                options={usersData}
                getOptionLabel={(option) => option.name}
                PopperComponent={filterAutocompletePopper}
                renderInput={(params) => (
                    <TextField {...params} label="Select Users" variant="outlined" size="small" />
                )}
            />
        </FormControl>
    );

    const getRoleOptions = () => {
        const salesRoles = [
            { value: ROLENAME.DGM_Sales, label: 'DGM Sales' },
            { value: ROLENAME.SalesManager, label: 'Sales Manager' },
            { value: ROLENAME.AssistantSalesManager, label: 'Assistant Sales Manager' },
            { value: ROLENAME.SalesUser, label: 'Executive' },
        ];
        const designRoles = [
            { value: ROLENAME.DGM_Design, label: 'DGM Design' },
            { value: ROLENAME.OperationDesignManager, label: 'Operation Design Manager' },
            { value: ROLENAME.AssistantDesignManager, label: 'Assistant Design Manager' },
            { value: ROLENAME.DesignUser, label: 'Designer' },
        ];
        const designRolesNoDgm = designRoles.slice(1);

        if (hasRole(ROLENAME.CenterHead, ROLENAME.BusinessHead, ROLENAME.DesignHead, ROLENAME.Admin, ROLENAME.DataAnalyst, ROLENAME.DGM_Sales)) {
            return [{ value: 'ALL', label: 'ALL' }].concat(
                userDetails?.departmentId === salesDepartment ? salesRoles : designRoles
            );
        }
        if (hasRole(ROLENAME.AGM_Design)) {
            return designRolesNoDgm;
        }
        if (hasRole(ROLENAME.SalesManager, ROLENAME.OperationDesignManager, ROLENAME.AGMSales)) {
            return userDetails?.departmentId === salesDepartment
                ? salesRoles.slice(1)
                : designRolesNoDgm;
        }
        if (hasRole(ROLENAME.AssistantSalesManager, ROLENAME.AssistantDesignManager)) {
            return userDetails?.departmentId === salesDepartment
                ? salesRoles.slice(2)
                : designRolesNoDgm.slice(1);
        }
        return null;
    };

    const renderRoleUsers = () => {
        const roleOptions = getRoleOptions();
        if (!roleOptions?.length) return null;

        const roleValue = selectedRole || 'ALL';
        const selectedRoleOption =
            roleOptions.find((option) => option.value === roleValue) || roleOptions[0];

        return (
            <>
                <div className={classes.filterCell}>
                    <FormControl className={classes.formControl}>
                        <Autocomplete
                            size="small"
                            disableClearable
                            options={roleOptions}
                            value={selectedRoleOption}
                            onChange={handleRoleAutocompleteChange}
                            getOptionLabel={(option) => option.label || ''}
                            getOptionSelected={(option, value) => option.value === value.value}
                            PopperComponent={filterAutocompletePopper}
                            renderInput={(params) => (
                                <TextField {...params} label="Select Role" variant="outlined" size="small" />
                            )}
                        />
                    </FormControl>
                </div>
                <div className={classes.filterCellGrow}>{usersAutocomplete}</div>
            </>
        );
    };

    const roleUsersFields = renderRoleUsers();

    const filterContent = (
        <div className={classes.filterRows}>
            <div className={classes.filterRowFlex}>
                {canPickLocation && (
                    <div className={classes.filterCellGrow}>
                        <FormControl className={classes.formControl}>
                            <Autocomplete
                                multiple
                                size="small"
                                value={selectedLocation}
                                onChange={handleMultipleLocationData}
                                options={commonLocation}
                                getOptionLabel={(option) => option.name}
                                PopperComponent={filterAutocompletePopper}
                                renderInput={(params) => (
                                    <TextField {...params} label="Select Location" variant="outlined" size="small" />
                                )}
                            />
                        </FormControl>
                    </div>
                )}
                {canPickExpCenter && (
                    <div className={classes.filterCellGrow}>
                        <FormControl className={classes.formControl}>
                            <Autocomplete
                                multiple
                                size="small"
                                value={selectedExperienceCenter}
                                onChange={handleMultipleExpData}
                                options={commonExpCenter}
                                getOptionLabel={(option) => option.name}
                                PopperComponent={filterAutocompletePopper}
                                renderInput={(params) => (
                                    <TextField {...params} label="Select Exp-Center" variant="outlined" size="small" />
                                )}
                            />
                        </FormControl>
                    </div>
                )}
                <div className={classes.filterCell}>
                    <TextField
                        name="startDate"
                        label="From Date"
                        type="date"
                        variant="outlined"
                        size="small"
                        value={storeDateFilter.startDate}
                        onChange={storePivotDate}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                    />
                </div>
                <div className={classes.filterCell}>
                    <TextField
                        name="endDate"
                        label="End Date"
                        type="date"
                        variant="outlined"
                        size="small"
                        value={storeDateFilter.endDate}
                        onChange={storePivotDate}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                    />
                </div>
                <div className={classes.filterCell}>
                    <Button
                        variant="contained"
                        onClick={handlePivotDateFilter}
                        className={`${classes.filterButtonBase} ${classes.actionBtn}`}
                    >
                        Submit
                    </Button>
                </div>
                <div className={classes.filterCell}>
                    <Button
                        variant="contained"
                        className={`${classes.filterButtonBase} ${classes.clearBtn}`}
                        onClick={clearDateFilter}
                    >
                        Clear Filter
                    </Button>
                </div>
            </div>

            <div className={classes.filterRowFlex}>
                <div className={classes.filterCell}>
                    <FormControl className={classes.formControl} variant="outlined" size="small">
                        <TextField
                            select
                            fullWidth
                            label="5% Payment"
                            variant="outlined"
                            size="small"
                            value={selectedValue === '' ? '2' : selectedValue}
                            onChange={handleSelectChange}
                            SelectProps={{ MenuProps: selectMenuProps }}
                        >
                            <MenuItem value="2">All</MenuItem>
                            <MenuItem value="1">YES</MenuItem>
                            <MenuItem value="0">NO</MenuItem>
                        </TextField>
                    </FormControl>
                </div>
                {roleUsersFields}
                <div className={classes.filterCell}>
                    <TextField
                        select
                        fullWidth
                        label="Search Type"
                        value={selectedSearchType}
                        onChange={(e) => handleSearchType(e.target.value)}
                        variant="outlined"
                        size="small"
                        SelectProps={{ MenuProps: selectMenuProps }}
                    >
                        {searchTypes.map((el) => (
                            <MenuItem key={el} value={el}>{el}</MenuItem>
                        ))}
                    </TextField>
                </div>
                <div className={classes.filterCellGrow}>
                    <TextField
                        fullWidth
                        label="Enter the input"
                        size="small"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        variant="outlined"
                    />
                </div>
                <div className={classes.filterCell}>
                    <Button
                        variant="contained"
                        disabled={!selectedSearchType || !searchInput}
                        className={`${classes.filterButtonBase} ${classes.searchBtn}`}
                        onClick={handleSearch}
                    >
                        Search
                    </Button>
                </div>
            </div>
        </div>
    );

    if (embedded) {
        return <div className={classes.embeddedRoot}>{filterContent}</div>;
    }

    return (
        <Paper className={classes.paper} elevation={0}>
            <Typography className={classes.sectionLabel}>Filters</Typography>
            {filterContent}
        </Paper>
    );
};

export default DesignSummaryFilterPanel;
